/**
 * Comment Setup - Initialization and Dependency Injection
 * Standardized setup pattern (Pattern B)
 * Follows unified initialization pattern across all modules
 */

import { CommentHandler } from "./comment-handler";
import { CommentListener } from "./comment-listener";
import { TextareaMentionSetup } from "./comment-mention-setup";
import { TextareaMentionListener } from "./comment-mention-listener";

interface CommentSetupInstance {
    handler: CommentHandler;
}

// Module-level singleton
let commentSetupInstance: CommentSetupInstance | null = null;

class CommentSetup {
    /**
     * Initialize comment system with dependency injection
     * Standard Pattern B: static init() method
     */
    public static init(): CommentSetupInstance {
        // 1. Create handler instance
        const handler = new CommentHandler();

        // 2. Bind handler methods for cleanup and event listener setup
        handler.submitHandler = handler.handleSubmit.bind(handler);
        handler.deleteHandler = handler.handleDelete.bind(handler);
        handler.replyToggleHandler = handler.handleReplyToggle.bind(handler);
        handler.dropdownHandler = handler.handleDropdown.bind(handler);
        handler.editHandler = handler.handleEdit.bind(handler);
        handler.cancelEditHandler = handler.handleCancelEdit.bind(handler);

        // 3. Setup event listeners (static method)
        CommentListener.setup(handler);

        // 4. Initialize mention autocomplete on all comment textareas (initial load)
        TextareaMentionSetup.initAll(document);

        // 5. Re-initialize mention on dynamically inserted content (modals, AJAX)
        TextareaMentionListener.bindContentInserted((root) => {
            TextareaMentionSetup.initAll(root);
        });

        // 6. Store instance for getter function
        commentSetupInstance = { handler };

        return commentSetupInstance;
    }
}

/**
 * Get CommentSetup singleton instance
 */
export function getCommentSetup(): CommentSetupInstance | null {
    return commentSetupInstance;
}

export { CommentSetup };
